import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, KeyRound, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { getErrorMessage } from '@/lib/errors';

interface Props {
  razon: string | null;
}

const RAZON_MSG: Record<string, string> = {
  EXPIRADA:         'La licencia de la plataforma ha expirado.',
  SUSPENDIDA:       'La licencia ha sido suspendida.',
  NO_ENCONTRADA:    'El código de activación no fue encontrado en el servidor de licencias.',
  DOMINIO_INVALIDO: 'El dominio de la plataforma no coincide con la licencia registrada.',
  ERROR_CONEXION:   'No se pudo verificar la activación del sistema.',
  ERROR_RESPUESTA:  'La respuesta del servidor de activación no es válida.',
};

function ActivarForm() {
  const qc = useQueryClient();
  const [codigo, setCodigo] = useState('');

  const mut = useMutation({
    mutationFn: (code: string) =>
      api.post('/api/suscripcion/activar', { codigoActivacion: code }).then(r => r.data),
    onSuccess: () => {
      toast.success('Código registrado. Verificando con Neej...');
      qc.invalidateQueries({ queryKey: ['suscripcion-verificar'] });
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al registrar el código')),
  });

  return (
    <div className="w-full space-y-3 mt-1">
      <input
        value={codigo}
        onChange={e => setCodigo(e.target.value.toUpperCase())}
        placeholder="Ej. ACT-XXXX-XXXX-XXXXXX"
        className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm font-mono tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-primary-300 uppercase"
        onKeyDown={e => e.key === 'Enter' && codigo.trim() && mut.mutate(codigo.trim())}
      />
      <button
        onClick={() => mut.mutate(codigo.trim())}
        disabled={!codigo.trim() || mut.isPending}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50"
      >
        {mut.isPending ? 'Activando...' : 'Activar plataforma'}
        {!mut.isPending && <ArrowRight size={15} />}
      </button>
    </div>
  );
}

export function SuscripcionBlockModal({ razon }: Props) {
  const esSinRegistro = razon === 'SIN_REGISTRO';
  const mensaje = (razon && RAZON_MSG[razon]) ?? 'La plataforma no se encuentra activa.';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900/70 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-8 flex flex-col items-center text-center gap-5">

        {/* Icono */}
        <div className={`w-16 h-16 rounded-full flex items-center justify-center ${esSinRegistro ? 'bg-primary-50' : 'bg-red-50'}`}>
          {esSinRegistro
            ? <KeyRound size={32} className="text-primary-500" />
            : <AlertCircle size={32} className="text-red-500" />
          }
        </div>

        {/* Título */}
        <div>
          <h2 className="text-xl font-bold text-text-primary">
            {esSinRegistro ? 'Licencia no configurada' : 'Plataforma inactiva'}
          </h2>
          <p className="mt-2 text-sm text-text-secondary leading-relaxed">
            {esSinRegistro
              ? 'Ingresa el código de activación proporcionado para comenzar a usar la plataforma.'
              : mensaje}
          </p>
        </div>

        {/* Formulario de activación o aviso de soporte */}
        {esSinRegistro ? (
          <ActivarForm />
        ) : (
          <div className="w-full px-4 py-3 rounded-xl bg-red-50 border border-red-100">
            <p className="text-xs text-red-700 leading-relaxed">
              Contacte al administrador del sistema o a soporte para reactivar la licencia.
            </p>
          </div>
        )}

        {/* Marca */}
        <p className="text-[11px] text-gray-300 mt-2 font-medium tracking-wide">
          Anik Health Platform
        </p>
      </div>
    </div>
  );
}
